// ============================================
// TRANSFER HOOKS
// ============================================

import {useEffect, useMemo} from 'react';
import {FieldInputProps} from 'formik';
import {omit} from 'lodash-es';
import {mainPlanTypeType, builderFieldsType} from '../CreateBuilder/builderFields';
import {
  transferFieldsType,
  transferSourceType,
} from '../BuilderTransfer/builderTransferFields';
import {OvbuilderDetails_ovrecommendDetails} from '../../../schema-types/OvbuilderDetails';
import {
  optionalRound,
  findParentOfChild,
} from '../CreateBuilder/BuilderSourceDestination/builderUtils';
import {getPotInfo, PotInfo} from '../../../utils/getPotInfo';
import {defaultFeeFields} from '../CreateBuilder/BuilderPlanFees';
import {nullableBooleanToYesNoEmpty} from '../BuilderTransfer/builderNullableBooleanToYesNoEmpty';
import {getFeeFormikFromServer} from '../CreateBuilder/BuilderDAInitialFees';
import {UPDATES} from '../../../shared/UPDATES';
import {
  useAutoForm,
  useAutoField,
  AutoFieldHelpers,
  SetFieldValueWithSourceKey,
} from '../../../components/AutoForm/useAutoForm';
import usePlanNamesProviders from '../../../hooks/usePlanNamesProviders';
import {useSourcesAndDestinationPots} from '../../../hooks/useSourcesAndDestinationPots';
import {moneyFieldValueType} from '../../../components/AutoForm/AutoFormNew';
import {BuilderPot, BuilderPot_dentistChildren} from '../../../schema-types/BuilderPot';
import {Pot} from '../../../schema-types/Pot';
import {useBuilderContext} from '../CreateBuilder/useBuilderContext';

// ============================================
// Destination
// ============================================

export const changeTransferDestination = ({
  pot,
  transferDestinationFieldHelpers,
}: {
  pot: BuilderPot | BuilderPot_dentistChildren;
  transferDestinationFieldHelpers: AutoFieldHelpers<
    transferFieldsType['destinationValues']
  >;
}) => {
  const parentPotId = 'dentistChildren' in pot ? undefined : pot.parent?.id ?? undefined;
  transferDestinationFieldHelpers.setValue(UPDATES._410, {
    existingPotId: pot.id,
    parentPotId,
  });
};

// ============================================
// Plan-type / pension defaults
// ============================================

export const resetTransferSourcesOnPlanTypeChange = ({
  transferSourcesFieldHelpers,
}: {
  transferSourcesFieldHelpers: AutoFieldHelpers<
    transferFieldsType['transferSources'] | undefined
  >;
}) => {
  transferSourcesFieldHelpers.setValue(UPDATES._406, {}, true);
};

export const resetDestinationIfInSources = ({
  checkedListSources,
  transferDestinationFieldObj,
  transferDestinationFieldHelpers,
}: {
  checkedListSources: {total: string[]};
  transferDestinationFieldObj: {
    value: transferFieldsType['destinationValues'] | undefined;
  };
  transferDestinationFieldHelpers: AutoFieldHelpers<
    transferFieldsType['destinationValues']
  >;
}) => {
  const existingPotId = transferDestinationFieldObj.value?.existingPotId;
  if (existingPotId && checkedListSources.total.includes(existingPotId)) {
    transferDestinationFieldHelpers.setValue(UPDATES._408, {
      existingPotId: null,
      parentPotId: undefined,
    });
  }
};

export const addShellSourcePlan = ({
  checkedListSources,
  setFieldValue,
  shellProducts,
  mainPlanTypeValue,
}: {
  checkedListSources: {shellPlans: string[]};
  setFieldValue: SetFieldValueWithSourceKey<builderFieldsType>;
  shellProducts: {label: string; value: string}[];
  mainPlanTypeValue: mainPlanTypeType;
}) => {
  const numberOfShells = checkedListSources.shellPlans.length;
  const id = `shell-${numberOfShells + 1}`;

  let defaultPlanType: string | undefined;
  if (mainPlanTypeValue === 'pension' && shellProducts.length) {
    const giaGross = shellProducts.find((x) =>
      x.label.toLocaleLowerCase().includes('gia gross')
    );
    defaultPlanType = giaGross?.value;
  }

  const newSource: transferSourceType = {
    id,
    singleClientAmount: '',
    maxAmount: 0,
    checked: true,
    isShellPlan: true,
    planFees: {
      feeDeviation: false,
      DFMFeeId: null,
      productFeeRequired: null,
      DFMFeeRequired: null,
      FPFeeId: null,
      FPFeeRequired: null,
      productFeeId: null,
      planServerFee: null,
    },
    legacyInitialFee: null,
    initialFee: undefined,
    full: true,
    inSpecie: false,
    contactId: '',
    isAuthorityHeld: '',
    isDataGather: '',
    planType: defaultPlanType,
  };
  setFieldValue(UPDATES._409, `transfer.transferSources.${id}`, newSource);
};

export const useResetTransferSourcesOnPlanTypeChange = ({
  transferSourcesFieldHelpers,
}: {
  transferSourcesFieldHelpers: AutoFieldHelpers<
    transferFieldsType['transferSources'] | undefined
  >;
}) => {
  const {values} = useAutoForm<builderFieldsType>();
  const {builderDetails} = useBuilderContext();
  const loadedPlanType = builderDetails?.planType;
  useEffect(() => {
    // still initializing — `useLoadBuilderMainPlanType` hasn't populated mainPlanType yet
    if (!values.mainPlanType) {
      return;
    }
    // loaded value matches current — user hasn't changed plan type
    if (loadedPlanType === values.mainPlanType) {
      return;
    }
    resetTransferSourcesOnPlanTypeChange({transferSourcesFieldHelpers});
  }, [values.mainPlanType, loadedPlanType]);
};

export const useResetDestinationIfInSources = ({
  checkedListSources,
  transferDestinationFieldObj,
  transferDestinationFieldHelpers,
}: {
  checkedListSources: {total: string[]};
  transferDestinationFieldObj: {
    value: transferFieldsType['destinationValues'] | undefined;
  };
  transferDestinationFieldHelpers: AutoFieldHelpers<
    transferFieldsType['destinationValues']
  >;
}) => {
  useEffect(() => {
    resetDestinationIfInSources({
      checkedListSources,
      transferDestinationFieldObj,
      transferDestinationFieldHelpers,
    });
  }, [transferDestinationFieldObj.value?.existingPotId, checkedListSources]);
};

// ============================================
// Source factory (child or parent-with-no-subplans)
// ============================================

export const getTotalTransferAmount = (sources: transferSourceType[]): number =>
  sources.reduce(
    (accumulator, currentValue) => accumulator + Number(currentValue?.singleClientAmount),
    0
  );

export const useDeriveCombinedSingleAmount = ({
  transferSources,
  combinedSingleAmountHelpers,
}: {
  transferSources: transferFieldsType['transferSources'] | undefined;
  combinedSingleAmountHelpers: AutoFieldHelpers<moneyFieldValueType | undefined>;
}) => {
  const total = getTotalTransferAmount(Object.values(transferSources ?? {}));
  useEffect(() => {
    combinedSingleAmountHelpers.setValue(UPDATES._450, total || '');
  }, [total]);
};

export const updateTransferSourceIdFactory = ({
  transferSourcesFieldObj,
  sourcePots,
  transferSourcesFieldHelpers,
  ovrecommendid,
}: {
  transferSourcesFieldHelpers: AutoFieldHelpers<
    transferFieldsType['transferSources'] | undefined
  >;
  sourcePots: BuilderPot[];
  transferSourcesFieldObj: FieldInputProps<
    transferFieldsType['transferSources'] | undefined
  >;
  ovrecommendid: string | false;
}): ((id: string) => void) => {
  return (id: string) => {
    // toggle off if already selected
    if (transferSourcesFieldObj.value && transferSourcesFieldObj.value[id]?.checked) {
      transferSourcesFieldHelpers.setValue(
        UPDATES._401,
        omit(transferSourcesFieldObj.value, id)
      );
      return;
    }

    // resolve whether `id` is a dentist child or a parent-with-no-subplans
    const parent = findParentOfChild(sourcePots, id);
    const child = parent?.dentistChildren?.find((c) => c.id === id);
    const parentNoSubplans = parent ? undefined : sourcePots.find((p) => p.id === id);
    const pot = child ?? parentNoSubplans;
    if (!pot) return;

    const maxAmount = pot.totalValue ? Number(pot.totalValue) : 0;
    const potInfo =
      ovrecommendid && parentNoSubplans
        ? getPotInfo(parentNoSubplans as unknown as Pot, ovrecommendid)
        : false;

    // note shell plans can only be parents with no subplans
    const isExistingShellPlanMadeInThisRecc =
      !!parentNoSubplans?.isShellPlan &&
      !!parentNoSubplans?.isDummy &&
      potInfo === PotInfo.existingShellPlanThisRec;

    const newSource: transferSourceType = {
      id,
      parentPotId: parent?.id,
      singleClientAmount: maxAmount || '',
      maxAmount,
      checked: true,
      isShellPlan: isExistingShellPlanMadeInThisRecc,
      planFees: defaultFeeFields,
      initialFee: undefined,
      legacyInitialFee: null,
      full: true,
      inSpecie: false,
      contactId: '',
      isAuthorityHeld: nullableBooleanToYesNoEmpty(pot.isAuthorityHeld),
      isDataGather: nullableBooleanToYesNoEmpty(pot.isDataGather),
    };
    transferSourcesFieldHelpers.setValue(UPDATES._401, {
      ...transferSourcesFieldObj.value,
      [id]: newSource,
    });
  };
};

// ============================================
// Load (initialValueOverrides)
// ============================================

export const getTransferInitialValues = (
  builderDetails: OvbuilderDetails_ovrecommendDetails | null,
  isSPPDA: boolean
): transferFieldsType => {
  const defaults: transferFieldsType = {
    transferSources: {},
    destinationValues: {existingPotId: null, parentPotId: undefined},
    combinedSingleAmount: '',
    isAps: false,
    isDependantsPension: false,
    isPensionSharing: false,
  };
  if (!builderDetails?.transfer) return defaults;
  const {destinationPot, existingSources, newSources} = builderDetails.transfer;

  const transferSources: transferFieldsType['transferSources'] = {};

  const existingSourcesList = existingSources ?? [];
  existingSourcesList.forEach((s) => {
    if (!s?.sourcePot) return;
    transferSources[s.sourcePot.id] = {
      id: s.sourcePot.id,
      parentPotId: s.sourcePot.parent?.id ?? undefined,
      singleClientAmount: optionalRound(s.singleClientAmount),
      full: s.isFull ?? false,
      inSpecie: s.isInSpecie ?? undefined,
      maxAmount: Number(s.sourcePot.totalValue ?? 0),
      contactId: s.sourcePot.contact?.id ?? '',
      checked: true,
      isShellPlan: false,
      planValue: s.sourcePot.currentDirectValue
        ? Number(s.sourcePot.currentDirectValue)
        : undefined,
      isAuthorityHeld: nullableBooleanToYesNoEmpty(s.sourcePot.isAuthorityHeld),
      isDataGather: nullableBooleanToYesNoEmpty(s.sourcePot.isDataGather),
      initialFee: s.initialFee?.id,
      legacyInitialFee: getFeeFormikFromServer(isSPPDA, s.sppInitialFee ?? null),
      planFees: defaultFeeFields,
    };
  });

  const newSourcesList = newSources ?? [];
  newSourcesList.forEach((s, index) => {
    if (!s) return;
    const id = `shell-${index}`;
    transferSources[id] = {
      id,
      isShellPlan: true,
      checked: true,
      singleClientAmount: optionalRound(s.singleClientAmount),
      accountNumber: s.planNumber,
      planProvider: s.productProvider?.id,
      planType: s.productName?.id,
      full: s.isFull ?? false,
      inSpecie: s.isInSpecie ?? undefined,
      contactId: s.contact?.id ?? 'joint',
      maxAmount: 0,
      isAuthorityHeld: nullableBooleanToYesNoEmpty(s.isAuthorityHeld),
      isDataGather: nullableBooleanToYesNoEmpty(s.isDataGather),
      planValue: Number(s.currentPlanValue ?? 0) || 0,
      initialFee: s.initialFee?.id,
      legacyInitialFee: getFeeFormikFromServer(isSPPDA, s.sppInitialFee ?? null),
      planFees: defaultFeeFields,
    };
  });

  const combinedSingleAmount = Object.values(transferSources).reduce(
    (acc, s) => acc + (Number(s.singleClientAmount) || 0),
    0
  );

  return {
    transferSources,
    destinationValues: {
      existingPotId: destinationPot?.id ?? null,
      parentPotId: destinationPot?.parent?.id ?? undefined,
    },
    combinedSingleAmount: combinedSingleAmount || '',
    isAps: builderDetails.isAps ?? false,
    isDependantsPension: builderDetails.isDependantsPension ?? false,
    isPensionSharing: builderDetails.isPensionSharing ?? false,
  };
};

// ============================================
// Source-form support hooks
// ============================================

export const setTransferFullAmount = ({
  transferSourceField,
  maxAmount,
  transferSourceHelpers,
}: {
  transferSourceField: {value: transferSourceType | undefined};
  maxAmount: moneyFieldValueType;
  transferSourceHelpers: AutoFieldHelpers<transferSourceType | undefined>;
}) => {
  if (transferSourceField.value?.full && maxAmount) {
    transferSourceHelpers.setValue(UPDATES._414, {
      ...transferSourceField.value,
      singleClientAmount: String(maxAmount) || '',
    });
  }
};

export const deriveIsLisaTransferSource = ({
  lisaId,
  values,
  sourcesAndDestinations,
  setFieldValue,
}: {
  lisaId: string | undefined;
  values: builderFieldsType;
  sourcesAndDestinations: {
    id: string;
    productName?: {formattedName?: string | null} | null;
  }[];
  setFieldValue: SetFieldValueWithSourceKey<builderFieldsType>;
}) => {
  let isLisa = false;
  if (!lisaId || !values.transfer?.destinationValues?.existingPotId) {
    isLisa = false;
  } else {
    const destId = values.transfer.destinationValues.existingPotId;
    isLisa =
      sourcesAndDestinations.find((x) => x.id === destId)?.productName?.formattedName ===
      'L-ISA';
  }
  setFieldValue(UPDATES._405, 'isLisa', isLisa);
  return isLisa;
};

export const useSetTransferFullAmount = ({
  id,
  maxAmount,
}: {
  id: string | undefined;
  maxAmount: moneyFieldValueType;
}) => {
  const [transferSourceField, , transferSourceHelpers] = useAutoField<
    transferSourceType | undefined
  >(`transfer.transferSources[${id}]`);
  useEffect(() => {
    setTransferFullAmount({transferSourceField, maxAmount, transferSourceHelpers});
  }, [transferSourceField.value?.full, maxAmount]);
};

export const useDeriveIsLisaTransferSource = () => {
  const {values, setFieldValue} = useAutoForm<builderFieldsType>();
  const {sourcesAndDestinations} = useSourcesAndDestinationPots();
  const {lisaId, queryGetProducts} = usePlanNamesProviders();
  useEffect(() => {
    if (!lisaId) {
      queryGetProducts({variables: {}});
    }
  }, [lisaId]);
  const isLisaDestination = useMemo(() => {
    return deriveIsLisaTransferSource({
      lisaId,
      values,
      sourcesAndDestinations,
      setFieldValue,
    });
  }, [
    lisaId,
    values.transfer?.destinationValues?.existingPotId,
    sourcesAndDestinations.length,
  ]);
  return isLisaDestination;
};
